import { useState, useEffect, useRef } from 'react';
import { Camera } from 'lucide-react';
import { Portal } from '../../components/boothly/portal.jsx';
import { SheetShell } from '../../components/boothly/sheet-shell.jsx';
import { InsetGroup } from '../../components/boothly/inset-list.jsx';

export function NeuesItemSheet({ open, onOpenChange, onCreateItem, categories, dark, store, authToken }) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const defaultCategories = ['Vintage', 'Bücher', 'Deko', 'Kleidung', 'Elektronik', 'Sonstiges'];
  const allCategories = (categories && categories.length > 0) ? categories : defaultCategories;

  useEffect(() => {
    if (open) {
      setName('');
      setPrice('');
      setCategory('');
      setNotes('');
      setPhotoFile(null);
      setPhotoPreview(null);
    }
  }, [open]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  if (!open) return null;

  const handleSave = async () => {
    let photoBlob = null;
    if (photoFile) {
      try {
        const { compressImage } = await import('../../photo-utils.js');
        photoBlob = await compressImage(photoFile);
      } catch (_e) { /* compression failure is non-fatal, skip photo */ }
    }
    onCreateItem?.({ name, wish_price: parseFloat(price), price: parseFloat(price), category, notes }, photoBlob);
    onOpenChange(false);
  };

  return (
    <Portal>
      <SheetShell
        open={open}
        onClose={() => onOpenChange(false)}
        grouped
        testId="neues-item-sheet"
        title="Neuer Artikel"
        footer={
          <button
            onClick={handleSave}
            disabled={!name || !price}
            className="h-[50px] w-full rounded-[14px] bg-[var(--accent-troedel)] text-[17px] font-semibold text-white shadow-[var(--shine-button)] active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100"
            style={{ transition: 'transform 100ms var(--ease-out-ios), opacity 150ms' }}
          >
            Artikel speichern
          </button>
        }
      >
        <div className="space-y-6 pb-4 pt-1">
          {/* Foto */}
          <div>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoChange} className="hidden" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex h-36 w-full items-center justify-center overflow-hidden rounded-[16px] bg-[var(--bg-L1)] active:opacity-80 dark:bg-[var(--bg-L2)]"
            >
              {photoPreview ? (
                <img src={photoPreview} alt="Vorschau" className="h-full w-full object-cover" />
              ) : (
                <span className="flex flex-col items-center gap-1.5 text-[var(--accent-troedel)]">
                  <Camera className="h-7 w-7" />
                  <span className="text-[15px] font-semibold">Foto aufnehmen</span>
                  <span className="text-[13px] text-[var(--label-secondary)]">oder aus der Mediathek wählen</span>
                </span>
              )}
            </button>
          </div>

          <InsetGroup surface="L2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name, z.B. Alte Kamera"
              aria-label="Name"
              enterKeyHint="next"
              className="h-[52px] w-full bg-transparent px-4 text-[17px] outline-none placeholder:text-[var(--label-tertiary)]"
            />
            <label className="ml-4 flex h-[52px] items-center gap-3 border-t border-[var(--separator)] pr-4">
              <span className="shrink-0 text-[17px]">Wunschpreis</span>
              <input
                type="number"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                className="num no-spin min-w-0 flex-1 bg-transparent text-right text-[17px] font-semibold outline-none placeholder:text-[var(--label-tertiary)]"
              />
            </label>
          </InsetGroup>

          <InsetGroup title="Kategorie" surface="L2">
            <div className="flex flex-wrap gap-2 p-3">
              {allCategories.map(cat => {
                const active = category === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    aria-pressed={active}
                    className={`h-11 rounded-full px-4 text-[15px] font-semibold ${
                      active ? 'bg-[var(--accent-troedel)] text-white' : 'bg-[var(--fill-tertiary)] text-[var(--label-primary)]'
                    }`}
                    style={{ transition: 'background-color 150ms var(--ease-out-ios), color 150ms' }}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </InsetGroup>

          <InsetGroup title="Notizen" surface="L2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional, z.B. leichte Gebrauchsspuren"
              rows={2}
              className="block w-full resize-none bg-transparent px-4 py-3 text-[17px] outline-none placeholder:text-[var(--label-tertiary)]"
            />
          </InsetGroup>
        </div>
      </SheetShell>
    </Portal>
  );
}
